import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form'; // Added useWatch
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getCustomerById, createCustomer, updateCustomer } from '@/lib/api'; // Removed unused Customer type from import
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

// Esquema de validação do formulário (removido city, state, postal_code)
const customerFormSchema = z.object({
  name: z.string().min(2, 'Nome é obrigatório'),
  phone: z.string().min(10, 'Telefone é obrigatório'),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  customer_type: z.enum(['pessoa_fisica', 'pessoa_juridica']).default('pessoa_fisica'),
  cpf: z.string().optional().or(z.literal('')), // Added CPF/CNPJ field
  address: z.string().optional().or(z.literal('')),
  // REMOVED: city, state, postal_code
  notes: z.string().optional().or(z.literal('')),
}).refine(data => {
    // Basic CPF/CNPJ validation based on type (can be improved)
    const cleanedCpfCnpj = data.cpf?.replace(/\D/g, '') || '';
    if (data.customer_type === 'pessoa_fisica' && cleanedCpfCnpj && cleanedCpfCnpj.length !== 11) {
        return false;
    }
    if (data.customer_type === 'pessoa_juridica' && cleanedCpfCnpj && cleanedCpfCnpj.length !== 14) {
        return false;
    }
    return true;
}, {
    message: "CPF/CNPJ inválido para o tipo de cliente selecionado",
    path: ["cpf"], // Assign error to the cpf field
});

type CustomerFormValues = z.infer<typeof customerFormSchema>;

const CustomerForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const isEditMode = !!id;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control, // Added control for useWatch
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    // Removido city, state, postal_code from default values
    defaultValues: {
      customer_type: 'pessoa_fisica',
      name: '',
      phone: '',
      email: '',
      cpf: '',
      address: '',
      notes: '',
    },
  });
  
  // Watch the customer_type field to change the label dynamically
  const customerType = useWatch({ control, name: 'customer_type' });
  const cpfLabel = customerType === 'pessoa_juridica' ? 'CNPJ' : 'CPF';

  useEffect(() => {
    if (isEditMode) {
      const loadCustomer = async () => {
        console.log(`Form: Attempting to load customer with ID: ${id}`);
        try {
          setIsLoading(true);
          // Ensure getCustomerById fetches 'cpf' (API function updated)
          const customer = await getCustomerById(id);
          console.log("Form: Customer data received from API:", customer);

          if (customer) {
            setValue('name', customer.name);
            setValue('phone', customer.phone);
            setValue('email', customer.email || '');
            setValue('cpf', customer.cpf || ''); // Set CPF value
            setValue('address', customer.address || '');
            // REMOVED: setValue for city, state, postal_code
            setValue('customer_type', (customer.customer_type as any) || 'pessoa_fisica');
            setValue('notes', customer.notes || '');
            console.log("Form: Fields populated successfully.");
          } else {
            console.error("Form: Customer not found in API response.");
            toast.error('Cliente não encontrado');
            navigate('/admin/customers');
          }
        } catch (error) {
          console.error('Erro ao carregar cliente:', error);
          toast.error('Erro ao carregar dados do cliente');
          // Don't navigate away immediately on error, let user see the form maybe?
          // navigate('/admin/customers'); 
        } finally {
          setIsLoading(false);
        }
      };
      loadCustomer();
    }
  }, [id, isEditMode, navigate, setValue]);

  const onSubmit = async (data: CustomerFormValues) => {
    console.log("Form: Submitting data:", data);
    try {
      setIsSaving(true);
      
      // customerData now correctly omits city, state, postal_code
      // as they are not in CustomerFormValues anymore.
      const customerData = {
        name: data.name,
        phone: data.phone,
        email: data.email || undefined,
        cpf: data.cpf || undefined, // Include CPF
        address: data.address || undefined,
        customer_type: data.customer_type,
        notes: data.notes || undefined
      };

      console.log("Form: Prepared data for API:", customerData);
      
      if (isEditMode) {
        console.log(`Form: Calling updateCustomer for ID: ${id}`);
        // Ensure updateCustomer handles 'cpf' and omits non-existent fields (API function updated)
        await updateCustomer(id, customerData);
        toast.success('Cliente atualizado com sucesso', { duration: 1000 });
      } else {
        console.log("Form: Calling createCustomer");
        // Ensure createCustomer handles 'cpf' and omits non-existent fields (API function updated)
        await createCustomer(customerData);
        toast.success('Cliente criado com sucesso', { duration: 1000 });
      }
      
      setTimeout(() => {
        navigate('/admin/customers');
      }, 500);
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      toast.error(
        isEditMode ? 'Erro ao atualizar cliente' : 'Erro ao criar cliente',
        { description: error instanceof Error ? error.message : 'Erro desconhecido' , duration: 3000 }
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="icon" 
            className="h-8 w-8"
            onClick={() => navigate('/admin/customers')}
            title="Voltar"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-2xl font-bold">
            {isEditMode ? 'Editar Cliente' : 'Novo Cliente'}
          </h2>
        </div>
        
        <Button 
          onClick={handleSubmit(onSubmit)} 
          disabled={isSaving || isLoading}
        >
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Salvar
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Informações do Cliente</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Carregando dados...</p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nome <span className="text-red-500">*</span></Label>
                    <Input id="name" {...register('name')} placeholder="Nome completo" className={errors.name ? 'border-red-500' : ''} />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="customer_type">Tipo de Cliente</Label>
                    <Select 
                      value={customerType} // Controlled component
                      onValueChange={(value) => setValue('customer_type', value as 'pessoa_fisica' | 'pessoa_juridica', { shouldValidate: true })} // Trigger validation on change
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pessoa_fisica">Pessoa Física</SelectItem>
                        <SelectItem value="pessoa_juridica">Pessoa Jurídica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cpf">{cpfLabel}</Label> {/* Dynamic Label */}
                    <Input 
                       id="cpf" 
                       {...register('cpf')} 
                       placeholder={`Digite o ${cpfLabel}`} 
                       className={errors.cpf ? 'border-red-500' : ''}
                       // You might want to add a masking library here (e.g., react-input-mask)
                     />
                    {errors.cpf && <p className="text-red-500 text-sm mt-1">{errors.cpf.message}</p>}
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                   <div>
                    <Label htmlFor="phone">Telefone <span className="text-red-500">*</span></Label>
                    <Input id="phone" {...register('phone')} placeholder="(99) 99999-9999" className={errors.phone ? 'border-red-500' : ''} />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" {...register('email')} placeholder="email@exemplo.com" className={errors.email ? 'border-red-500' : ''} />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
                  </div>
                </div>
              </div>
              
              <Separator />
              <CardTitle className="text-lg">Endereço</CardTitle>
              
              {/* REMOVED City, State, Zip section */}
              <div className="grid grid-cols-1 gap-6">
                 {/* Address Field (now spans full width) */}
                 <div className="space-y-4">
                   <div>
                    <Label htmlFor="address">Logradouro</Label>
                    <Input id="address" {...register('address')} placeholder="Rua, número, complemento" />
                    {/* Removed error display for address if not needed, or keep if address is required */}
                  </div>
                 </div>
              </div>

              <Separator />

              <div>
                <Label htmlFor="notes">Observações</Label>
                <Textarea id="notes" {...register('notes')} placeholder="Informações adicionais..." rows={4} />
              </div>

              {/* Keep Save/Cancel buttons outside the inner grid */}
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigate('/admin/customers')}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSaving || isLoading}>
                  {isSaving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Salvar
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerForm;
